/**
 * Element / ring colour palette — must match `BrandEquityVisualizationV3.tsx`
 * exactly. Used for SCORE-COLOURING (the number itself, dot indicator, top
 * border, focused outline) wherever a specific element is being displayed.
 *
 * IMPORTANT — band colours (BAND_PALETTE in `bandColours.ts`) are still used
 * for the BandTag pill (shows the classification name) and the gradient
 * gauge marker. Those communicate the BAND. Element colours communicate
 * WHICH ELEMENT the score belongs to (v3 convention).
 *
 * Variable names in v3 (`amber`, `green`, `orange`…) are misleading — the
 * hex values are all blues, navies, corals and purples. Names kept here for
 * 1:1 traceability to v3's ELEMENT_CONFIG.
 *
 * Source: `BrandEquityVisualizationV3.tsx:70-77` (palette) + 94-109 (mapping).
 */

const RING_COLORS = Object.freeze({
  amber: '#3d5a80', // Brand Equity (centre) — dark steel blue (despite the name)
  green: '#3d5a80', // Ring 1 (Integral): Brand Loyalty, Perceived Quality
  orange: '#8fb8d4', // Ring 2 (Transitional): Beh./Att. Loyalty, WTPP, Associations
  purple: '#b39ddb', // Market Awareness (special)
  red: '#e07a5f', // Ring 3 (Fundamental): Alignment Depth, Brand Attachment, PQV
  blue: '#1b2a41', // Ring 4 (Measured): All ~25 QC signals — dark navy
  grey: '#9ca3af', // Locked / no-data
});

type ColourGroup = keyof typeof RING_COLORS;

/**
 * Explicit element → colour-group mapping. Mirrors v3's `ELEMENT_CONFIG`
 * (`BrandEquityVisualizationV3.tsx:94-109`). Use canonical element names
 * (pre-display-rename) — i.e. `Alignment Depth` not `Touchpoints`.
 */
const ELEMENT_GROUP: Readonly<Record<string, ColourGroup>> = Object.freeze({
  'Brand Equity': 'amber',
  // Ring 1
  'Brand Loyalty': 'green',
  'Perceived Quality': 'green',
  // Ring 2
  'Behavioural Loyalty': 'orange',
  'Willingness to Pay a Premium': 'orange',
  Associations: 'orange',
  'Attitudinal Loyalty': 'orange',
  // Market Awareness — conditional element (purple)
  'Market Awareness': 'purple',
  // Ring 3
  'Alignment Depth': 'red',
  'Brand Attachment': 'red',
  'Quality Value Tradeoff (PQV)': 'red',
});

/**
 * Resolve a canonical element/QC name to its v3 ring colour. QCs (measured
 * elements not in the explicit map) fall through to Ring-4 navy (`blue`).
 * Locked elements should be marked through `locked: true` upstream and use
 * grey separately — this helper only handles the "available" case.
 */
export function getElementColour(canonical: string): string {
  const group = ELEMENT_GROUP[canonical];
  if (group) return RING_COLORS[group];
  // Default: measured QC → Ring 4 navy. Matches v3 lines 287-295 which
  // assigns ring 4 / blue to every questionCategoryScores entry.
  return RING_COLORS.blue;
}

/** Locked / no-data colour. */
export function getLockedColour(): string {
  return RING_COLORS.grey;
}

/**
 * Score-display colour helper:
 * • Score is null → grey
 * • Otherwise → element ring colour
 *
 * This replaces band-colour-for-score everywhere a specific element is being
 * rendered. The marketer view's status framing (strong / watch / weak) is
 * communicated through the BandTag pill + gauge marker, not the number's
 * colour. See `bandColours.ts` for the band palette.
 */
export function colourForScore(
  canonical: string,
  value: number | null | undefined,
): string {
  if (value == null) return RING_COLORS.grey;
  return getElementColour(canonical);
}
